<template>
  <div class="promotions-slider section-gradient-wrapper">
    <div class="flex items-center justify-between">
      <ol-icon name="promo-mobile" :size="24" class="ol-mr-sm" color="var(--cas-light-900)" />
      <div class="section__title">{{ $t('promo') }}</div>
      <div class="flex items-center">
        <router-link :to="{ name: 'Promotions', params: { type: 'all' } }" class="section__link">
          {{ $t('all') }}
        </router-link>
        <ol-slider-button
          class="ol-ml-lg"
          :disabled-left="disabledLeftBtn"
          :disabled-right="disabledRightBtn"
          @click-left="prevSlide"
          @click-right="nextSlide"
        />
      </div>
    </div>

    <swiper
      class="swiper-promo"
      v-bind="swiperOptions"
      @progress="onProgress"
      @swiper="onSwiper"
      @breakpoint="onBreakpoint"
    >
      <template v-if="!promoItems.length">
        <swiper-slide v-for="i in 4" :key="i" class="swiper-promo__wrapper-slide">
          <promo-skeleton class="swiper-slide swiper-promo__slide" />
        </swiper-slide>
      </template>
      <template v-else>
        <swiper-slide v-for="promo in promoItemsFiltered" :key="promo.id" class="swiper-promo__wrapper-slide">
          <promo-card class="swiper-slide swiper-promo__slide" :promo="promo" @expired-card="expiredCard" />
        </swiper-slide>
      </template>
    </swiper>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { Swiper, SwiperSlide } from 'swiper/vue'
import PromoCard from 'components/promo/PromoCard'
import PromoSkeleton from 'components/promo/PromoSkeleton.vue'
import { OlSliderButton, OlIcon } from '@gamesite/olimp-core'
import { useQuery } from '@vue/apollo-composable'
import getContentListWithDates from 'src/apollo/queries/getContentListWithDates.gql'
import { fillPromoItems } from 'src/common/functions'
import { usePromoStore } from 'src/stores/promo'

const promoStore = usePromoStore()
const swiperOptions = {
  preloadImages: false,
  pagination: false,
  loop: false,
  freeMode: false,
  freeModeSticky: false,
  spaceBetween: 0,
  threshold: 10,
  breakpoints: {
    1025: {
      slidesPerView: 4,
    },
    768: {
      slidesPerView: 3,
    },
    521: {
      slidesPerView: 3,
    },
    0: {
      slidesPerView: 1.7,
    },
  },
}

const swiperInstance = ref(null)

function onSwiper(swiper) {
  swiperInstance.value = swiper
}

const progress = ref()
const disabledLeftBtn = computed(() => progress.value <= 0)
const disabledRightBtn = computed(() => progress.value >= 1)

const promoItemsRaw = ref(null)

function getPromotions({ fromNetwork }) {
  const { onResult, onError } = useQuery(
    getContentListWithDates,
    { type: 'promotion_v2' },
    fromNetwork ? { fetchPolicy: 'network-only' } : null
  )
  onResult((result) => {
    if (result.data) {
      promoItemsRaw.value = result.data.propsContentList
    }
  })
  onError((error) => {
    console.error(error)
  })
}

getPromotions({ fromNetwork: false })

const currentTime = ref(new Date().getDate())
const promoItems = computed(() => fillPromoItems(promoItemsRaw.value, promoStore.promoTypes))
const promoItemsFiltered = computed(() => {
  return promoItems.value.filter((item) => {
    if (item.dateEnd) {
      const dateEnd = new Date(item.dateEnd).getTime()

      const timeUntilExpiration = dateEnd - currentTime.value
      return !(timeUntilExpiration <= 0 || !timeUntilExpiration)
    }
    return true
  })
})

const slidesPerView = ref(4)

function onBreakpoint(swiper, breakpointParams) {
  slidesPerView.value = Math.ceil(breakpointParams.slidesPerView) || 4
}

function onProgress(instance, progressValue) {
  progress.value = progressValue
}

function nextSlide() {
  swiperInstance.value.slideNext()
}

function prevSlide() {
  swiperInstance.value.slidePrev()
}

function expiredCard() {
  currentTime.value = new Date().getTime()
}
</script>

<style lang="scss">
.promotions-slider {
  overflow: hidden;
}

.swiper-promo {
  margin: 0 -4px 0 -4px;

  @media (max-width: 520px) {
    overflow: visible;
  }

  &__wrapper-slide {
    height: unset;
    display: flex;
    border: 4px solid transparent;
    border-top-width: 0;
  }

  &__slide {
    height: unset;
  }

  .promo-item {
    width: 100%;
  }
}
</style>
